import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// PlanPicker archetype — plan catalog selector. Uses host adapter
// `adapters.fetchPlans(locationKey)` to retrieve the plan list; reads
// `payload.locationKey` (or whatever config.locationKeyPath points to)
// to scope the lookup. Sets `payload[planIdPath]` on selection.
//
// Control archetype — owns its own Continue button, advances the stage
// via `advance({ [planIdPath]: id })` directly. Does not participate in
// the per-stage form aggregator.
import { useEffect, useState } from 'react';
import { s } from './common.js';
const FALLBACK_PLANS = [
    {
        id: 'plan_performance',
        name: 'Performance Package — Symmetrical 200 Mbps',
        monthlyPriceCents: 12900,
        speedDownMbps: 200,
        speedUpMbps: 200,
        restricted: false,
    },
    {
        id: 'plan_ultimate',
        name: 'Ultimate Experience — Symmetrical 2.5 Gbps',
        monthlyPriceCents: 17900,
        speedDownMbps: 2500,
        speedUpMbps: 2500,
        restricted: false,
    },
];
function getPath(payload, path) {
    return path.split('.').reduce((acc, key) => {
        if (acc && typeof acc === 'object' && key in acc) {
            return acc[key];
        }
        return undefined;
    }, payload);
}
export function PlanPickerComponent(props) {
    const { config, payload, adapters, advance, back, isAdvancing } = props;
    const planIdPath = s(config, 'planIdPath', 'planId');
    const locationKeyPath = s(config, 'locationKeyPath', 'locationKey');
    const locationKey = getPath(payload, locationKeyPath) ?? 'unmapped';
    const initialPlan = getPath(payload, planIdPath);
    const [plans, setPlans] = useState(null);
    const [selected, setSelected] = useState(typeof initialPlan === 'string' ? initialPlan : null);
    useEffect(() => {
        let cancelled = false;
        if (!adapters.fetchPlans) {
            setPlans(FALLBACK_PLANS);
            return;
        }
        adapters
            .fetchPlans(locationKey)
            .then((items) => {
            if (!cancelled)
                setPlans(items);
        })
            .catch(() => {
            if (!cancelled)
                setPlans(FALLBACK_PLANS);
        });
        return () => {
            cancelled = true;
        };
    }, [adapters, locationKey]);
    if (!plans) {
        return _jsx("div", { className: "text-sm text-[var(--bcbb-text-muted)]", children: "Loading plans\u2026" });
    }
    return (_jsxs("div", { className: "flex flex-col gap-4", children: [_jsx("p", { className: "m-0 text-sm text-[var(--bcbb-text-muted)]", children: "Pick a plan to continue. You can change this later from your portal." }), _jsx("div", { className: "flex flex-col gap-3", children: plans.map((p) => {
                    const checked = selected === p.id;
                    return (_jsxs("label", { className: `flex cursor-pointer items-start justify-between gap-4 rounded-xl border-2 bg-white p-4 transition-colors ${checked ? 'border-[var(--bcbb-comms)]' : 'border-[var(--bcbb-border)]'}`, children: [_jsxs("div", { children: [_jsx("div", { className: "font-semibold", children: p.name }), _jsxs("div", { className: "mt-1 text-sm text-[var(--bcbb-text-muted)]", children: [p.speedDownMbps.toLocaleString(), " / ", p.speedUpMbps.toLocaleString(), " Mbps \u00B7 symmetrical"] })] }), _jsxs("div", { className: "flex items-center gap-3", children: [_jsxs("div", { className: "text-right", children: [_jsxs("div", { className: "font-semibold", children: ["$", (p.monthlyPriceCents / 100).toFixed(2)] }), _jsx("div", { className: "text-xs text-[var(--bcbb-text-muted)]", children: "/month" })] }), _jsx("input", { type: "radio", name: "plan", className: "h-4 w-4 accent-[var(--bcbb-comms)]", checked: checked, onChange: () => setSelected(p.id) })] })] }, p.id));
                }) }), _jsxs("div", { className: "mt-4 flex items-center justify-between gap-3", children: [_jsx("button", { type: "button", onClick: back, disabled: isAdvancing, className: "rounded-md border bg-white px-4 py-2 text-sm font-medium", style: { borderColor: 'var(--bcbb-border)' }, children: "Back" }), _jsx("button", { type: "button", onClick: () => {
                            if (selected)
                                void advance({ [planIdPath]: selected });
                        }, disabled: !selected || isAdvancing, className: "rounded-md px-5 py-3 text-base font-semibold text-white disabled:opacity-60", style: { background: 'var(--bcbb-primary)' }, children: isAdvancing ? 'Saving…' : 'Continue' })] })] }));
}
export const PlanPickerArchetype = {
    name: 'PlanPicker',
    label: 'Plan picker',
    description: 'Plan-catalog-backed selector. Stores chosen planId on the payload.',
    component: PlanPickerComponent,
    isFormArchetype: false,
    fields: {
        planIdPath: { kind: 'text', label: 'Plan id payload key', defaultValue: 'planId' },
        locationKeyPath: { kind: 'text', label: 'Location key payload key', defaultValue: 'locationKey' },
    },
    defaultConfig: { planIdPath: 'planId', locationKeyPath: 'locationKey' },
};
